import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2 } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function ConsultantLoginPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/consultant/login", { email, password });
      toast.success(`Welcome, ${data.name || ""}`.trim());
      window.location.href = data.must_change_password
        ? "/consultant/set-password" : "/consultant";
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-center px-14 bg-zinc-50 border-r border-zinc-200">
        <div className="flex items-center gap-2 mb-10">
          <div className="h-9 w-9 rounded-md bg-emerald-600 grid place-items-center">
            <Building2 className="h-5 w-5 text-white" strokeWidth={1.75} />
          </div>
          <span className="font-display text-lg font-bold">Arborvitae</span>
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight leading-tight">
          Your consults,<br /><span className="text-emerald-600">clearly accounted for.</span>
        </h1>
        <p className="mt-4 text-sm text-zinc-500 max-w-md">
          Sign in to see the cases you handled each month and what is payable to you.
        </p>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <h2 className="font-display text-2xl font-bold">Doctor sign in</h2>
          <p className="mt-1 text-sm text-zinc-500">
            Use the email and password Arborvitae gave you.
          </p>

          {error ? (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              data-testid="con-login-error">{error}</div>
          ) : null}

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <Label className="text-xs text-zinc-500">Email</Label>
              <Input type="email" value={email} autoComplete="username"
                onChange={(e) => setEmail(e.target.value)} className="h-10"
                data-testid="con-login-email" required />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Password</Label>
              <PasswordInput value={password} autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)} className="h-10"
                data-testid="con-login-password" required />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-10"
              data-testid="con-login-submit">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-[11px] text-zinc-500">
            Forgotten your password? Contact Arborvitae and they will issue a new one.
          </p>
          <p className="mt-2 text-[11px] text-zinc-400">
            Arborvitae staff sign in <a href="/login" className="underline">here</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
